'use client';

import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@/lib/mui';
import {
  ReturnsPreferences,
  ReturnsPreferencesReturnMethodsEnum,
} from '@/entities/preferences/returns';
import { PreferenceDomain } from '@/entities/preferences';
import { updateUserPreferences } from '@/hooks/useUpdateUserPreferences';
import { UserProfile } from '@/services/customerProfileService.types';
import useTitanAnalytics from '@/hooks/useTitanAnalytics';
import { returnOptions } from './config';
import ReturnsIcon from './_icon';
import Section from '../Section';

interface Props {
  user?: UserProfile;
  preferences?: ReturnsPreferences;
}

export default function ReturnLocations({ user, preferences }: Props) {
  const [selected, setSelected] =
    useState<ReturnsPreferencesReturnMethodsEnum | null>(
      preferences?.return_methods[0] ?? null,
    );

  const { mutate } = useMutation({
    mutationFn: updateUserPreferences,
  });

  useEffect(() => {
    setSelected(preferences?.return_methods[0] ?? null);
  }, [preferences]);

  const titan = useTitanAnalytics();

  const handleChange = async (value: ReturnsPreferencesReturnMethodsEnum) => {
    setSelected(value);

    mutate({
      product: PreferenceDomain.Returns,
      body: {
        carriers: preferences?.carriers ?? [],
        locations: preferences?.locations ?? [],
        return_methods: [value],
      },
    });

    titan
      .track({
        userId: user?.profile_id,
        event: 'product_clicked',
        channel: 'web',
        type: 'track',
        context: {},
        properties: {
          sku: 'Return',
          variant: value,
          narvar_product_context: 'track',
        },
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
  };

  const onClear = async () => {
    setSelected(null);

    mutate({
      product: PreferenceDomain.Returns,
      body: {
        carriers: preferences?.carriers ?? [],
        locations: preferences?.locations ?? [],
        return_methods: [],
      },
    });

    titan
      .track({
        userId: user?.profile_id,
        event: 'product_removed',
        channel: 'web',
        type: 'track',
        context: {},
        properties: {
          sku: 'Return',
          variant: 'clear',
          narvar_product_context: 'track',
        },
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
  };

  return (
    <Section
      Icon={ReturnsIcon}
      title="Returns Preferences"
      desc="Save your return methods & preferences to use for future returns with retailers who use Narvar."
      style={{ marginTop: '2rem' }}
    >
      <Box
        display="flex"
        mt={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle2">Select your top preference</Typography>
        <Button
          sx={{ pr: '1rem' }}
          variant="text"
          onClick={onClear}
          color="secondary"
        >
          Clear
        </Button>
      </Box>

      <Box mt={4} display="flex">
        <RadioGroup
          row
          aria-labelledby="demo-form-control-label-placement"
          name="position"
          value={selected}
          onChange={(e) =>
            handleChange(e.target.value as ReturnsPreferencesReturnMethodsEnum)
          }
          style={{ width: '100%', display: 'block' }}
        >
          {returnOptions.map((option) => {
            const IconComponent = option.icon;

            return (
              <FormControlLabel
                key={option.key}
                value={option.key}
                style={{ width: '100%' }}
                sx={{
                  '.MuiFormControlLabel-label': {
                    flex: 1,
                  },
                }}
                control={
                  <Radio
                    color="secondary"
                    style={{
                      alignSelf: 'flex-start',
                      justifyContent: 'flex-end',
                    }}
                  />
                }
                label={
                  <Box
                    mr={2}
                    mb={4}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                  >
                    <Box>
                      <IconComponent />
                    </Box>
                    <Box ml={2} flex={1}>
                      <Typography variant="body1">{option.title}</Typography>
                    </Box>
                  </Box>
                }
                labelPlacement="start"
              />
            );
          })}
        </RadioGroup>
      </Box>
    </Section>
  );
}
